import { db } from './config'
import { collection, getDocs, query, orderBy } from 'firebase/firestore'
import { getAuthHeaders } from './authHeaders'

const SHOPIFY_API_BASE = process.env.REACT_APP_PAYMENT_API || 'https://api-yleo2sn4ca-uc.a.run.app'

// A Shopify sale's receiver is the store's role id, not the owner's uid, so
// any "this user's sales" view has to match the uid plus every store the
// account owns. shopify_settings holds each store's access token and is not
// client-readable, so the mapping comes from the backend.
const storeSellerIdCache = new Map()

export const getSellerIdsForUser = async (uid) => {
    if (!uid) return []
    if (storeSellerIdCache.has(uid)) return storeSellerIdCache.get(uid)

    try {
        const response = await fetch(
            `${SHOPIFY_API_BASE}/shopify/my-stores?uid=${encodeURIComponent(uid)}`,
            { headers: { ...(await getAuthHeaders()) } }
        )
        const payload = await response.json()
        const ids = [uid, ...(payload?.stores || []).map((store) => store.seller_id).filter(Boolean)]
        storeSellerIdCache.set(uid, ids)
        return ids
    } catch (error) {
        console.error('Error resolving store seller ids:', error)
        return [uid]
    }
}

// Every connected Shopify store with its owner -- the account that store's
// sales are credited to. Stores live in shopify_settings keyed by shop
// domain, alongside each store's OAuth token.
export const getShopifyStores = async () => {
    const response = await fetch(`${SHOPIFY_API_BASE}/shopify/stores`, {
        headers: { ...(await getAuthHeaders()) }
    })
    const payload = await response.json()

    if (!response.ok || payload?.success === false) {
        throw new Error(payload?.error || 'Failed to load connected Shopify stores')
    }

    return payload.stores || []
}

// Raised when the outgoing owner still has unshipped orders on that store.
// Carries the handover counts so the dialog can spell out what changes, and
// is resolved by calling setStoreOwner again with confirmed = true.
export class UnshippedOrdersError extends Error {
    constructor(message, handover) {
        super(message)
        this.name = 'UnshippedOrdersError'
        this.handover = handover || {}
    }
}

export const setStoreOwner = async (shop, ownerEmail, { confirmed = false } = {}) => {
    const response = await fetch(`${SHOPIFY_API_BASE}/shopify/stores/owner`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(await getAuthHeaders())
        },
        body: JSON.stringify({ shop, owner_email: ownerEmail, confirmed })
    })
    const payload = await response.json()

    if (payload?.code === 'unshipped_orders_pending') {
        throw new UnshippedOrdersError(payload.error, payload.handover)
    }

    if (!response.ok || payload?.success === false) {
        throw new Error(payload?.error || 'Failed to update the store owner')
    }

    return payload
}

const postJson = async (path, body, fallbackError) => {
    const response = await fetch(`${SHOPIFY_API_BASE}${path}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(await getAuthHeaders())
        },
        body: JSON.stringify(body)
    })
    const payload = await response.json()

    if (!response.ok || payload?.success === false) {
        throw new Error(payload?.error || fallbackError)
    }

    return payload
}

// Blocks or unblocks a whole store. Distinct from the per-listing switch its
// owner controls: this one the owner cannot lift.
export const setStoreBlocked = (shop, blocked) =>
    postJson(`/shopify/admin/stores/${encodeURIComponent(shop)}/blocked`, { blocked }, 'Failed to update the store')

// One store's listings with the price a shopper actually pays (its Shopify
// price times the store's multiplier) and the owner's visibility switch.
export const getStoreListings = async (shop) => {
    const response = await fetch(
        `${SHOPIFY_API_BASE}/shopify/admin/stores/${encodeURIComponent(shop)}/listings`,
        { headers: { ...(await getAuthHeaders()) } }
    )
    const payload = await response.json()

    if (!response.ok || payload?.success === false) {
        throw new Error(payload?.error || 'Failed to load store listings')
    }

    return payload
}

// Pulls one store's catalogue and stock levels into Firestore.
//
// Takes an explicit shop: with several stores connected, a sync that omits it
// falls back to the single SHOPIFY_STORE_DOMAIN env var and would refresh the
// wrong merchant's products.
//
// syncProductsToFirestore already fetches per-location inventory alongside the
// catalogue, so this covers both.
export const syncStore = async (shop, { limit = 50 } = {}) => {
    const response = await fetch(`${SHOPIFY_API_BASE}/shopify/sync-products`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(await getAuthHeaders())
        },
        body: JSON.stringify({ shop, limit })
    })
    const payload = await response.json()

    if (!response.ok || payload?.success === false) {
        throw new Error(payload?.message || payload?.error || 'Shopify sync failed')
    }

    return payload
}

export const getShopifyProducts = async () => {
    try {
        const shopifyProductsRef = collection(db, 'shopify_products')
        const q = query(shopifyProductsRef, orderBy('title'))
        const snapshot = await getDocs(q)
        const products = []

        snapshot.forEach((doc) => {
            // A shopify_products document carries its own `id` field (the raw
            // Shopify product id), so the document id is applied after the
            // spread or it gets overwritten.
            products.push({ ...doc.data(), id: doc.id })
        })

        return products
    } catch (error) {
        console.error('Error fetching Shopify products:', error)
        return []
    }
}
